import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  changeMembersRole,
  changeMembersRoleUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { DirectoryGroup, DirectoryGroupMember } from '../../../../../types';
import GeneralDialog from '../../../../shared/general-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Alert from '@mui/material/Alert';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface ChangeGroupMemberRoleDialogProps {
  group: DirectoryGroup;
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  groupEmail: string;
  memberList: DirectoryGroupMember[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  userView: boolean;
}

export default function ChangeGroupMemberRoleDialog({
  open,
  handleClose,
  setSelectedRows,
  selectedRows,
  groupEmail,
  group,
  memberList,
  changeLogRoles,
  userView,
}: ChangeGroupMemberRoleDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [selectedRole, setSelectedRole] = useState<
    'MEMBER' | 'OWNER' | 'MANAGER'
  >('MEMBER');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      changeMembersRole(
        groupEmail,
        'Group',
        noGroupMembers,
        selectedRole,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: noGroupMembers.length > 25 ? 'info' : 'success',
        message:
          noGroupMembers.length > 25
            ? 'The task to update the group members roles has begun.'
            : 'Group members role(s) updated successfully!',
      });

      if (noGroupMembers.length <= 25) {
        queryClient.setQueryData(
          [UserKeys.GroupMembers, groupEmail],
          (oldData: DirectoryGroupMember[]) => {
            return oldData.map((member) => {
              if (
                selectedRows.includes(member.email) &&
                member.type !== 'group'
              ) {
                return {
                  ...member,
                  role: selectedRole,
                };
              } else {
                return member;
              }
            });
          }
        );
      }

      setSelectedRole('MEMBER');
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the selected members role(s). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.update_member_role, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () =>
        changeMembersRoleUserPortal(
          groupEmail,
          'Group',
          noGroupMembers,
          selectedRole
        ),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: noGroupMembers.length > 25 ? 'info' : 'success',
          message:
            noGroupMembers.length > 25
              ? 'The task to update the group members roles has begun.'
              : 'Group members role(s) updated successfully!',
        });

        if (noGroupMembers.length <= 25) {
          queryClient.setQueryData(
            [UserKeys.GroupMembers, groupEmail],
            (oldData: DirectoryGroupMember[]) => {
              return oldData.map((member) => {
                if (
                  selectedRows.includes(member.email) &&
                  member.type !== 'group'
                ) {
                  return {
                    ...member,
                    role: selectedRole,
                  };
                } else {
                  return member;
                }
              });
            }
          );
        }

        setSelectedRole('MEMBER');
        setSelectedRows([]);
        handleClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue updating the selected members role(s). Please refresh and try again.',
        });
      },
      onSettled: () => {
        createEvent(analytics.userView.group_details.update_member_role, {
          module: ModuleList.modules.userView.name,
          section: ModuleList.modules.userView.sections.groupDetails,
        });
      },
    });

  function handleSubmit() {
    if (
      !userView &&
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditGroupMemberRole.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      if (userView) {
        userPortalMutate();
      } else {
        mutate();
      }
    }
  }

  const selectedMembers = memberList.filter((member) =>
    selectedRows.includes(member.email)
  );

  const displayNote = selectedMembers.some((member) => member.type === 'group');

  const noGroupMembers = selectedMembers.filter(
    (member) => member.type !== 'group'
  );

  const RoleOptions = [
    { value: 'MEMBER', displayValue: 'Member' },
    { value: 'MANAGER', displayValue: 'Manager' },
    { value: 'OWNER', displayValue: 'Owner' },
  ];

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        setSelectedRole('MEMBER');
        handleClose();
      }}
      title="Change Role"
      dialogContent={
        <Stack sx={{ px: 4 }} gap={3} alignItems={'center'}>
          <Typography>
            {selectedRows.length > 1
              ? `Select a new role for the selected ${selectedRows.length} members from ${group.email}:`
              : selectedMembers.length && selectedMembers[0].email === '*'
              ? `Select a new role for "All current and future users within domain" from ${group.email}:`
              : `Select a new role for ${selectedRows[0]} from ${group.email}:`}
          </Typography>
          <TextField
            label="Role"
            select
            value={selectedRole}
            onChange={(event) =>
              setSelectedRole(
                event.target.value as 'MEMBER' | 'MANAGER' | 'OWNER'
              )
            }
            sx={{ width: 400 }}
          >
            {RoleOptions.map((option) => (
              <MenuItem value={option.value} key={option.displayValue}>
                {option.displayValue}
              </MenuItem>
            ))}
          </TextField>
          <Grow
            in={
              displayNote &&
              (selectedRole === 'MANAGER' || selectedRole === 'OWNER')
            }
            unmountOnExit
          >
            <Alert severity="info">
              Groups can not be set to be Managers or Owners of a group. All
              selected groups will be ignored.
            </Alert>
          </Grow>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Update',
        action: handleSubmit,
        loading: isPending || userPortalPending,
        disabled: isPending || userPortalPending,
      }}
    />
  );
}
